import 'slick-carousel/slick/slick.css';

import classnames from 'classnames';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Slider, { Settings } from 'react-slick';

import {
	Button,
	Field,
	Input,
	InputMessage,
	Label,
} from '@ao-internal/components-react';

import ChatbotShapeSmileIcon from '../assets/icons/ChatbotShapeSmile';
import useFeedback, { FeedbackStageType } from '../hooks/useFeedback';
import { Feedback } from '../services/FeedbackService';
import CheckboxScoreButtons, {
	CheckboxScoreValue,
} from './forms/CheckboxScoreButtons';

interface FeedbackFormProps {
	feedback: Feedback;
	onClose: () => void;
	isReadOnly?: boolean;
}

enum FormStatus {
	Idle,
	Loading,
	SubmissionFailed,
	Submitted,
}

enum FormSectionTypes {
	RelevanceScore,
	DetailScore,
	GoalScore,
	Comments,
}

type FormData = Pick<
	Feedback,
	'relevanceScore' | 'detailScore' | 'goalScore' | 'comments'
>;
type ScoreFieldKeys = 'relevanceScore' | 'detailScore' | 'goalScore';

const slideIndexes = Object.keys(FormSectionTypes)
	.map((section) => parseInt(section, 10))
	.filter(Number.isInteger);

export const maxCharacterLength = 256;

function getCurrentFormSection(feedbackData: Feedback): FormSectionTypes {
	if (!feedbackData.relevanceScore) {
		return FormSectionTypes.RelevanceScore;
	}
	if (!feedbackData.detailScore) {
		return FormSectionTypes.DetailScore;
	}
	if (!feedbackData.goalScore) {
		return FormSectionTypes.GoalScore;
	}
	return FormSectionTypes.Comments;
}

function generateScoreValues(
	formData: FormData,
	baseValues: CheckboxScoreValue[],
	property: ScoreFieldKeys
): CheckboxScoreValue[] {
	if (!formData.hasOwnProperty(property)) {
		return [];
	}

	return baseValues.map((value) => ({
		...value,
		isChecked: formData[property] === parseInt(value.value, 10),
	}));
}

let feedbackCacheArray: Feedback[] = [];

export const FeedbackFormCarousel: React.FC<
	FeedbackFormProps & React.HTMLProps<HTMLDivElement>
> = ({ feedback, onClose, isReadOnly, ...props }) => {
	const { t } = useTranslation();
	const { saveFeedback } = useFeedback();

	const feedbackCache: undefined | Feedback =
		feedbackCacheArray && feedbackCacheArray.length > 0
			? feedbackCacheArray.filter(
					(storedFeedback) => storedFeedback.id === feedback.id
			  )[0]
			: undefined;

	const feedbackData = feedbackCache ? feedbackCache : feedback;

	const [formData, setFormData] = React.useState<FormData>({
		relevanceScore: feedbackData.relevanceScore,
		detailScore: feedbackData.detailScore,
		goalScore: feedbackData.goalScore,
		comments: feedbackData.comments,
	});
	const [commentFieldMessage, setCommentFieldMessage] =
		React.useState<string>();
	const [formStatus, setFormStatus] = React.useState<FormStatus>(
		FormStatus.Idle
	);

	const [currentFormSection, setCurrentFormSection] =
		React.useState<FormSectionTypes>(getCurrentFormSection(feedbackData));
	const [currentSlide, setCurrentSlide] = React.useState(currentFormSection);
	const sliderRef = React.useRef<Slider>(null);
	const sliderSettings: Settings = {
		adaptiveHeight: true,
		dots: false,
		arrows: false,
		infinite: false,
		speed: 500,
		accessibility: true,
		initialSlide: currentFormSection,
		beforeChange: (_currentSlide, nextSlide) => {
			setCurrentSlide(nextSlide);
		},
	};
	const baseScoreButtonValues: CheckboxScoreValue[] = [
		{
			label: '1',
			value: '1',
			ariaLabel: t('globals:stronglyDisagree'),
			isChecked: false,
		},
		{
			label: '2',
			value: '2',
			ariaLabel: t('globals:disagree'),
			isChecked: false,
		},
		{
			label: '3',
			value: '3',
			ariaLabel: t('globals:neutral'),
			isChecked: false,
		},
		{
			label: '4',
			value: '4',
			ariaLabel: t('globals:agree'),
			isChecked: false,
		},
		{
			label: '5',
			value: '5',
			ariaLabel: t('globals:stronglyAgree'),
			isChecked: false,
		},
	];

	const scoreButtonBoundaryLabels = {
		start: t('globals:disagree'),
		end: t('globals:agree'),
	};

	function handleScoreChange(
		evt: React.ChangeEvent<HTMLInputElement>,
		section: FormSectionTypes,
		stage: FeedbackStageType
	) {
		const newFormData = {
			...formData,
			[stage]: parseInt(evt.target.value, 10),
		};
		setFormData(newFormData);

		try {
			saveFeedback({ ...feedbackData, ...newFormData }, stage);
		} catch (error) {
			// TODO: Handle/log errors somewhere?
		} finally {
			setCurrentFormSection(section + 1);
			if (sliderRef.current) {
				sliderRef.current.slickNext();
			}
		}
	}

	function handleCommentChange(evt: React.ChangeEvent<HTMLTextAreaElement>) {
		let inputValue = evt.target.value;

		if (maxCharacterLength && inputValue.length > maxCharacterLength) {
			inputValue = inputValue.slice(0, maxCharacterLength);
		}

		setFormData((currentFormData) => ({
			...currentFormData,
			comments: inputValue,
		}));
	}

	function handleSubmit(evt: React.FormEvent) {
		evt.preventDefault();

		setCommentFieldMessage(undefined);

		setFormStatus(FormStatus.Loading);

		// TODO: Convert feedback stage to enum?
		saveFeedback({ ...feedbackData, ...formData }, 'comments')
			.then(() => {
				setFormStatus(FormStatus.Submitted);
			})
			.catch(() => {
				setFormStatus(FormStatus.SubmissionFailed);
			});
	}

	function handlePaginationClick(idx: number) {
		if (sliderRef.current) {
			setCurrentFormSection(idx);
			sliderRef.current.slickGoTo(idx);
		}
	}

	React.useEffect(() => {
		if (sliderRef.current) {
			// Required so that the carousel advances when clicking on a form checkbox after closing and re-opening
			sliderRef.current.slickGoTo(currentSlide);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (feedbackCacheArray && feedbackCacheArray.length > 0) {
			feedbackCacheArray = feedbackCacheArray.reduce((acc, item) => {
				if (item.id === feedback.id) {
					return [...acc, { ...item, ...formData }];
				}
				return [...acc, { ...feedback, ...formData }];
			}, [] as Feedback[]);
		} else {
			feedbackCacheArray.push({ ...feedback, ...formData });
		}
	}, [feedback, formData]);

	if (formStatus === FormStatus.Submitted) {
		return (
			<div data-testid="feedback-form" className="p-4" {...props}>
				<div className="text-center">
					<ChatbotShapeSmileIcon className="ico items-center justify-center w-8 h-8 mb-3" />

					<h3
						id="chatbot-feedback-form-title"
						className="text-title-lg mb-3"
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{ __html: t('feedbackForm:successTitle') }}
					/>

					<p className="text-body-sm text-primary mb-3">
						{t('feedbackForm:successIntro')}
					</p>

					<Button variant="tertiary" onClick={onClose}>
						{t('feedbackForm:closeCta')}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div data-testid="feedback-form" {...props}>
			<div className="p-4 pb-0 mb-3 text-center">
				<ChatbotShapeSmileIcon className="ico items-center justify-center w-8 h-8 mb-3" />

				<h3 id="chatbot-feedback-form-title" className="text-title-lg mb-3">
					{t('feedbackForm:formTitle')}
				</h3>

				<p
					className="text-body-sm text-primary"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: t('feedbackForm:formIntro') }}
				></p>
			</div>

			<form onSubmit={handleSubmit}>
				<div id="chatbot-feedback-form-carousel" aria-live="polite">
					<Slider ref={sliderRef} {...sliderSettings}>
						<div
							ref={(node) =>
								node &&
								(currentSlide === FormSectionTypes.RelevanceScore
									? node.removeAttribute('inert')
									: node.setAttribute('inert', ''))
							}
							data-testid="carousel-item-relevanceScore"
							className="flex items-center p-4 pt-0"
						>
							<div
								ref={(node) =>
									node && isReadOnly && node.setAttribute('inert', '')
								}
								className="w-full max-w-xs m-auto"
							>
								<CheckboxScoreButtons
									name="relevanceScore"
									values={generateScoreValues(
										formData,
										baseScoreButtonValues,
										'relevanceScore'
									)}
									label={t('feedbackForm:relevanceScoreLabel')}
									boundaryLabels={scoreButtonBoundaryLabels}
									handleChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
										if (evt.target.checked) {
											handleScoreChange(
												evt,
												FormSectionTypes.RelevanceScore,
												'relevanceScore'
											);
										}
									}}
									radioChoice
								/>
							</div>
						</div>
						<div
							ref={(node) =>
								node &&
								(currentSlide === FormSectionTypes.DetailScore
									? node.removeAttribute('inert')
									: node.setAttribute('inert', ''))
							}
							data-testid="carousel-item-detailScore"
							className="flex items-center p-4 pt-0"
						>
							<div
								ref={(node) =>
									node && isReadOnly && node.setAttribute('inert', '')
								}
								className="w-full max-w-xs m-auto"
							>
								<CheckboxScoreButtons
									name="detailScore"
									values={generateScoreValues(
										formData,
										baseScoreButtonValues,
										'detailScore'
									)}
									label={t('feedbackForm:detailScoreLabel')}
									boundaryLabels={scoreButtonBoundaryLabels}
									handleChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
										if (evt.target.checked) {
											handleScoreChange(
												evt,
												FormSectionTypes.DetailScore,
												'detailScore'
											);
										}
									}}
									radioChoice
								/>
							</div>
						</div>
						<div
							ref={(node) =>
								node &&
								(currentSlide === FormSectionTypes.GoalScore
									? node.removeAttribute('inert')
									: node.setAttribute('inert', ''))
							}
							data-testid="carousel-item-goalScore"
							className="flex items-center p-4 pt-0"
						>
							<div
								ref={(node) =>
									node && isReadOnly && node.setAttribute('inert', '')
								}
								className="w-full max-w-xs m-auto"
							>
								<CheckboxScoreButtons
									name="goalScore"
									values={generateScoreValues(
										formData,
										baseScoreButtonValues,
										'goalScore'
									)}
									label={t('feedbackForm:goalScoreLabel')}
									boundaryLabels={scoreButtonBoundaryLabels}
									handleChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
										if (evt.target.checked) {
											handleScoreChange(
												evt,
												FormSectionTypes.GoalScore,
												'goalScore'
											);
										}
									}}
									radioChoice
								/>
							</div>
						</div>
						<div
							ref={(node) =>
								node &&
								(currentSlide === FormSectionTypes.Comments
									? node.removeAttribute('inert')
									: node.setAttribute('inert', ''))
							}
							data-testid="carousel-item-comments"
							className="flex flex-col items-center w-full p-4 pt-0"
						>
							<div
								ref={(node) =>
									node && isReadOnly && node.setAttribute('inert', '')
								}
								className="w-full max-w-xs"
							>
								<Field name="comments">
									{({ inputProps, labelProps, messageProps }) => (
										<React.Fragment>
											<Label {...labelProps}>
												{t('feedbackForm:commentFieldLabel')}
											</Label>
											{commentFieldMessage && (
												<InputMessage {...messageProps} state="error">
													{commentFieldMessage}
												</InputMessage>
											)}
											<Input
												{...inputProps}
												as="textarea"
												className="w-full"
												placeholder={t('feedbackForm:commentFieldPlaceholder')}
												defaultValue={formData.comments}
												maxLength={maxCharacterLength}
												onChange={handleCommentChange}
												style={{ minHeight: '100px' }}
												state={commentFieldMessage ? 'error' : undefined}
											/>
										</React.Fragment>
									)}
								</Field>

								{formStatus === FormStatus.SubmissionFailed && (
									<p className="text-error mb-2 text-center">
										{t('feedbackForm:submissionFailed')}
									</p>
								)}

								<Button
									data-testid="submit-cta"
									className="justify-center w-full text-center"
									type="submit"
									disabled={formStatus === FormStatus.Loading}
								>
									{formStatus === FormStatus.Loading
										? `${t('feedbackForm:submitCtaLoading')}`
										: `${t('feedbackForm:submitCta')}`}
								</Button>
							</div>
						</div>
					</Slider>
				</div>

				{/* TODO: Create a pagination dots component */}
				<div className="flex justify-center pb-4 space-x-1">
					{slideIndexes.map((idx) => (
						<button
							key={idx}
							data-tag-name="feedback form carousel pagination"
							data-tag-position={idx + 1}
							type="button"
							onClick={() => handlePaginationClick(idx)}
							className="c-chatbot-pagination-button flex items-center justify-center p-0 bg-transparent rounded-full cursor-pointer"
							style={{ width: '44px', height: '44px' }}
							aria-controls="chatbot-feedback-form-carousel"
							aria-current={currentSlide === idx}
						>
							<span
								className={classnames(
									'c-chatbot-pagination-button__indicator block rounded-full',
									{
										'bg-cta-action': currentSlide === idx,
										'bg-gray-50': currentSlide !== idx,
									}
								)}
							></span>
							<span className="sr-only">
								{t('feedbackForm:paginationDotLabel', { slide: idx + 1 })}
							</span>
						</button>
					))}
				</div>
			</form>
		</div>
	);
};

export default FeedbackFormCarousel;
